import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Percent, Check, X, Pencil } from 'lucide-react';
import { apiErrorMessage, getAccrualBasis, setAccrualBasis, type AccrualBasis } from '../api/abfApi';
import { fmtDate, plural } from '../presentation/format';
import type { ActionFeedbackState } from '../components/ActionFeedback';

/**
 * WS7 D-9 — the accrual basis is a contract term: FACILITY_RATE (the facility all-in rate),
 * BLENDED_COLLATERAL_RATE (the eligible loans' balance-weighted rate on the latest released tape)
 * or BLENDED_CAPPED_AT_FACILITY. Changing it needs the clause and a checker; it changes how every
 * later day accrues and nothing before. Every accrual row says which basis priced it.
 */

interface Props {
  facilityId: number;
  canCheck: boolean;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

const Card = styled.section`
  margin-top: 12px; padding: 1rem; border: 1px solid rgba(148,163,184,.16); border-radius: 12px; background: rgba(8,16,30,.8);
  h3 { margin: 0; color: #f8fafc; font-size: 15px; }
  p.sub { margin: .3rem 0 0; color: #94a3b8; font-size: 12px; }
  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 10px; margin-top: .8rem; }
  .fact { padding: .5rem .6rem; border: 1px solid rgba(148,163,184,.12); border-radius: 10px; background: rgba(5,12,24,.4); }
  .fact .label { color: #94a8c2; font-size: 11px; text-transform: uppercase; letter-spacing: .05em; }
  .fact .value { margin-top: 2px; color: #f8fafc; font-size: 15px; font-weight: 650; font-variant-numeric: tabular-nums; }
  select, input { min-height: 34px; padding: .35rem .5rem; border: 1px solid rgba(148,163,184,.2); border-radius: 8px; background: #07101f; color: #e2e8f0; font-size: 13px; }
  button { display: inline-flex; align-items: center; gap: 6px; min-height: 34px; padding: .35rem .7rem; border: 1px solid rgba(148,163,184,.24); border-radius: 8px; color: #cbd5e1; background: rgba(15,23,42,.6); cursor: pointer; font: inherit; font-size: 13px; }
  button:disabled { opacity: .5; cursor: default; }
  button.primary { color: #e0f2fe; border-color: #7dd3fc; background: rgba(14,116,144,.25); }
  table { width: 100%; border-collapse: collapse; margin-top: .9rem; font-size: 12px; font-variant-numeric: tabular-nums; }
  th { text-align: left; padding: .4rem .5rem; color: #7dd3fc; font-size: 11px; font-weight: 650; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid rgba(148,163,184,.2); }
  td { padding: .4rem .5rem; border-top: 1px solid rgba(148,163,184,.1); color: #e2e8f0; }
  th.num, td.num { text-align: right; white-space: nowrap; }
  .muted { color: #94a3b8; } .boundary { margin-top: .7rem; color: #94a3b8; font-size: 11px; line-height: 1.5; }
  details { margin-top: .9rem; }
  details > summary { cursor: pointer; color: #bae6fd; font-size: 13px; list-style: none; display: flex; align-items: center; gap: 6px; }
  details > summary::-webkit-details-marker { display: none; }
  details > summary::before { content: '▸'; font-size: 11px; color: #7dd3fc; }
  details[open] > summary::before { content: '▾'; }
  details > summary small { color: #94a3b8; font-size: 12px; }
  details table { margin-top: .5rem; }
`;
const pct = (v: unknown) => v === null || v === undefined ? '—' : `${Number(v).toFixed(4).replace(/\.?0+$/, '')}%`;
const money = (v: unknown) => v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const words = (v: unknown) => String(v ?? '').toLowerCase().replace(/_/g, ' ');

const AccrualBasisPanel: React.FC<Props> = ({ facilityId, canCheck, onFeedback }) => {
  const [model, setModel] = useState<AccrualBasis | null>(null);
  const [editing, setEditing] = useState(false);
  const [basis, setBasis] = useState('FACILITY_RATE');
  const [clause, setClause] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try { setModel(await getAccrualBasis(facilityId)); }
    catch (error) { onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The accrual basis could not be loaded.') }); }
  }, [facilityId, onFeedback]);
  useEffect(() => { void load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try {
      setModel(await setAccrualBasis(facilityId, basis, clause.trim()));
      setEditing(false);
      onFeedback({ tone: 'success', message: `Accrual basis set to ${words(basis)}; every later day accrues on it. The term shows in the Terms table with its clause.` });
    } catch (error) {
      onFeedback({ tone: 'error', message: apiErrorMessage(error, 'The accrual basis could not be set.') });
    } finally { setBusy(false); }
  };
  if (!model) return null;
  return (
    <Card aria-label="Accrual basis">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <h3><Percent size={15} style={{ verticalAlign: 'middle', marginRight: 6 }} />Accrual basis</h3>
          <p className="sub">{model.note}</p>
        </div>
        {canCheck && !editing && <button type="button" onClick={() => { setBasis(model.basis); setClause(model.sourceClause || ''); setEditing(true); }}><Pencil size={14} />Set basis</button>}
      </div>
      <div className="grid">
        <div className="fact"><div className="label">Contract basis</div><div className="value">{words(model.basis)}</div><div className="muted" style={{ fontSize: 11 }}>{model.sourceClause ? `clause ${model.sourceClause}` : 'default (no clause stated)'}{model.activeVersionNo ? ` · version ${model.activeVersionNo}` : ''}</div></div>
        <div className="fact"><div className="label">Facility all-in rate</div><div className="value">{pct(model.facilityRatePct)}</div></div>
        <div className="fact"><div className="label">Collateral WAC (latest released tape)</div><div className="value">{pct(model.wacPct)}</div><div className="muted" style={{ fontSize: 11 }}>{model.wacTapeIngestId ? `tape ${model.wacTapeIngestId}` : 'no released tape with loan rates'}</div></div>
        <div className="fact"><div className="label">Rate in force today</div><div className="value">{pct(model.rateInForcePct)}</div></div>
      </div>
      {editing && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#94a8c2', fontSize: 11 }}>Basis
          <select aria-label="Accrual basis" value={basis} onChange={e => setBasis(e.target.value)}>{model.bases.map(b => <option key={b} value={b}>{words(b)}</option>)}</select></label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, color: '#94a8c2', fontSize: 11 }}>Source clause
          <input aria-label="Source clause" value={clause} onChange={e => setClause(e.target.value)} placeholder="e.g. §2.6(b) Interest" style={{ minWidth: 260 }} /></label>
        <button type="button" className="primary" disabled={busy || !clause.trim()} onClick={() => void save()}><Check size={14} />{busy ? 'Saving…' : 'Set as contract term'}</button>
        <button type="button" disabled={busy} onClick={() => setEditing(false)}><X size={14} />Cancel</button>
      </div>}
      {model.recentAccruals.length > 0 && <details>
        <summary>Recent daily accruals <small>{plural(model.recentAccruals.length, 'day')} · latest {fmtDate(String(model.recentAccruals[0]?.accrual_date))}</small></summary>
        <table>
        <thead><tr><th>Date</th><th className="num">Drawn</th><th className="num">Rate</th><th>Basis</th><th className="num">Facility rate</th><th className="num">WAC</th><th className="num">Interest</th></tr></thead>
        <tbody>{model.recentAccruals.map(a => <tr key={a.accrual_date}><td>{fmtDate(String(a.accrual_date))}</td><td className="num">{money(a.drawn_balance)}</td><td className="num">{pct(a.all_in_rate_pct)}</td><td className="muted">{words(a.accrual_basis)}</td><td className="num">{pct(a.facility_rate_pct)}</td><td className="num">{a.wac_pct == null ? '—' : `${pct(a.wac_pct)} (tape ${a.wac_tape_ingest_id})`}</td><td className="num">{money(a.interest_accrual)}</td></tr>)}</tbody>
        </table>
      </details>}
      <div className="boundary">{model.authorityBoundary}</div>
    </Card>
  );
};

export default AccrualBasisPanel;
